import { IconButton, Stack } from "@mui/material";
import { useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import MultiSelect from "./MultiSelect";
import { SearchRequest } from "../../../api/pps";
import TextField from "../../../components/atoms/TextField";
import SearchIcon from "../../../components/icons/ui-actions/SearchIcon";
import { usePermissionStore } from "../../../stores/permissionStore";
import {
  SearchState,
  useSearchQuery,
  useSearchStore,
  useSetSearchStore,
} from "../../../stores/ppsFormSearchStore";
import { mono } from "../../../theme/colors";

interface SearchBarProps {
  request: SearchRequest;
  setIsSearch: React.Dispatch<React.SetStateAction<boolean>>;
  setRequest: React.Dispatch<React.SetStateAction<SearchRequest>>;
  refetch: () => void;
}

const country = [
  { value: "ALL", label: "All" },
  { value: "TH", label: "TH" },
  { value: "JP", label: "JP" },
  { value: "ID", label: "ID" },
  { value: "MY", label: "MY" },
  { value: "KH", label: "KH" },
  { value: "PH", label: "PH" },
  { value: "HK", label: "HK" },
  { value: "VN", label: "VN" },
  { value: "MO", label: "MO" },
  { value: "SG", label: "SG" },
  { value: "Others", label: "Others" },
];

const SearchBar = ({
  request,
  setIsSearch,
  setRequest,
  refetch,
}: SearchBarProps) => {
  const SearchState = useSearchStore();
  const SearchStore = useSetSearchStore();
  const formMethods = useForm<SearchState>({
    defaultValues: SearchState,
  });
  const formValues = formMethods.getValues();

  const { ppsCountries } = usePermissionStore();
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const { setQueryString } = useSearchQuery();

  const optionsSelect = useMemo(() => {
    if (ppsCountries) {
      const allCountries = ppsCountries?.find(val => val === "ALL");
      if (allCountries) {
        return country;
      } else {
        return ppsCountries?.map(item => ({
          label: item,
          value: item,
        }));
      }
    }
  }, [ppsCountries]);

  const handleClickSearch = () => {
    setIsSearch(false);
    const values = formMethods.getValues();
    if (!values.omneId) {
      formMethods.setError("omneId", {
        message: "This field is required",
        type: "required",
      });
      return;
    }
    if (values.signUpCountry.length <= 0 || selectAll) {
      formMethods.setError("signUpCountry", {
        message: "This field is required",
        type: "required",
      });
      return;
    }
    setIsSearch(true);
    SearchStore(values);
    const value = {
      omneId: values.omneId,
      signUpCountry: values.signUpCountry.find(item => item === "ALL")
        ? "ALL"
        : values.signUpCountry.toString(),
    };
    setRequest(prev => ({ ...prev, ...value }));
    setQueryString({
      omneId: value.omneId,
      signUpCountry: value.signUpCountry,
      pagination: {
        currentPage: 1,
        pageSize: 12,
      },
      permission_Token: usePermissionStore.getState().permissionToken ?? "",
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    handleClickSearch();
  };

  const colorHelpText = useMemo(() => {
    if (
      !!formMethods.formState.errors.omneId?.message &&
      !!formMethods.formState.errors.signUpCountry?.message
    ) {
      return "#B30909";
    } else {
      return "#8B8E8F";
    }
  }, [formValues]);

  return (
    <Stack direction={"row"} justifyContent="left" spacing={2} paddingY={2}>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit} data-testid="customer-search-form">
          <Stack marginBottom={0.5} color={colorHelpText}>
            ( It is mandatory to fill the fields below )
          </Stack>
          <Stack spacing={2} direction={"row"}>
            <Stack width={180}>
              <TextField name="omneId" label="User Id" />
            </Stack>
            <Stack width={210}>
              <MultiSelect
                name="signUpCountry"
                label="Omne Signup Country"
                options={optionsSelect || []}
                changeValue={(value: boolean) => {
                  setSelectAll(value);
                }}
              />
            </Stack>
            <Stack spacing={2} justifyContent={"flex-start"}>
              <IconButton
                data-testid="inventory-homepage_product-Search-search_button"
                onClick={handleClickSearch}
                sx={{
                  backgroundColor: mono.white,
                  width: 40,
                  height: 40,
                }}
              >
                <SearchIcon />
              </IconButton>
            </Stack>
          </Stack>
          <input type="submit" hidden />
        </form>
      </FormProvider>
    </Stack>
  );
};

export default SearchBar;
